import { useEffect, useState } from 'react';
import { Plus, Trash2, Sparkles, X } from 'lucide-react';
import { planningApi } from './api';
import { PlanningPhaseTemplate, PlanningPhaseTemplateItem, PlanningDependencyOffsetType } from './types';

const EMPTY_ITEM: Partial<PlanningPhaseTemplateItem> = { name: '', orderIndex: 0, durationDays: 1, plannedHours: 0, dependsOnItemId: null, dependencyOffsetDays: 0, dependencyOffsetType: 'CALENDAR' };

export default function TemplatesTab() {
  const [templates, setTemplates] = useState<PlanningPhaseTemplate[]>([]);
  const [selected, setSelected] = useState<PlanningPhaseTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '' });
  const [itemForm, setItemForm] = useState<Partial<PlanningPhaseTemplateItem>>(EMPTY_ITEM);
  const [genForm, setGenForm] = useState({ expedientCode: '', expedientLabel: '', startDate: '' });
  const [showGenerate, setShowGenerate] = useState(false);

  const load = () => planningApi.listTemplates().then(setTemplates);
  useEffect(() => { load(); }, []);

  const openTemplate = async (t: PlanningPhaseTemplate) => setSelected(await planningApi.getTemplate(t.id));

  const createTemplate = async () => {
    if (!newTemplate.name.trim()) return;
    const t = await planningApi.createTemplate(newTemplate);
    setNewTemplate({ name: '', description: '' });
    await load();
    openTemplate(t);
  };

  const removeTemplate = async (t: PlanningPhaseTemplate) => {
    if (!confirm(`Eliminar la plantilla "${t.name}"?`)) return;
    await planningApi.removeTemplate(t.id);
    if (selected?.id === t.id) setSelected(null);
    load();
  };

  const addItem = async () => {
    if (!selected || !itemForm.name?.trim()) return;
    await planningApi.addTemplateItem(selected.id, { ...itemForm, orderIndex: selected.items?.length ?? 0 });
    setItemForm(EMPTY_ITEM);
    openTemplate(selected);
  };

  const removeItem = async (itemId: string) => {
    if (!selected) return;
    await planningApi.removeTemplateItem(selected.id, itemId);
    openTemplate(selected);
  };

  const generate = async () => {
    if (!selected || !genForm.startDate) return;
    const phases = await planningApi.generateFromTemplate(selected.id, genForm);
    alert(`S'han creat ${phases.length} fases a partir de la plantilla.`);
    setShowGenerate(false);
    setGenForm({ expedientCode: '', expedientLabel: '', startDate: '' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Plantilles de fases</h3>
          <div className="flex items-center gap-1.5">
            <input placeholder="Nom (p.ex. Construcció de skatepark)" value={newTemplate.name} onChange={(e) => setNewTemplate((f) => ({ ...f, name: e.target.value }))} className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5" />
            <button onClick={createTemplate} className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={14} /></button>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {templates.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Sense plantilles</div>
          ) : templates.map((t) => (
            <button key={t.id} onClick={() => openTemplate(t)} className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 ${selected?.id === t.id ? 'bg-blue-50' : ''}`}>
              <span className="text-xs font-medium text-gray-900">{t.name}</span>
              <span onClick={(e) => { e.stopPropagation(); removeTemplate(t); }} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={13} /></span>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg">
        {!selected ? (
          <div className="p-6 text-center text-xs text-muted-foreground">Selecciona o crea una plantilla</div>
        ) : (
          <div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">{selected.name}</h3>
              <button onClick={() => setShowGenerate((v) => !v)} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-green-600 text-white rounded hover:bg-green-700">
                <Sparkles size={13} /> Generar fases per un expedient
              </button>
            </div>

            {showGenerate && (
              <div className="p-4 border-b border-gray-100 bg-green-50 grid grid-cols-3 gap-2">
                <input placeholder="Codi expedient" value={genForm.expedientCode} onChange={(e) => setGenForm((f) => ({ ...f, expedientCode: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input placeholder="Etiqueta (nom descriptiu)" value={genForm.expedientLabel} onChange={(e) => setGenForm((f) => ({ ...f, expedientLabel: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="date" value={genForm.startDate} onChange={(e) => setGenForm((f) => ({ ...f, startDate: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <button onClick={generate} className="col-span-3 text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700">Generar</button>
              </div>
            )}

            <div className="p-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground text-left border-b border-gray-100">
                    <th className="py-1.5">#</th>
                    <th className="py-1.5">Fase</th>
                    <th className="py-1.5">Durada (dies)</th>
                    <th className="py-1.5">Hores previstes</th>
                    <th className="py-1.5">Dependència</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.items ?? []).sort((a, b) => a.orderIndex - b.orderIndex).map((it) => (
                    <tr key={it.id} className="border-b border-gray-50">
                      <td className="py-1.5">{it.orderIndex + 1}</td>
                      <td className="py-1.5 font-medium">{it.name}</td>
                      <td className="py-1.5">{it.durationDays}</td>
                      <td className="py-1.5">{it.plannedHours}</td>
                      <td className="py-1.5">
                        {it.dependsOnItemId
                          ? `${selected.items?.find((x) => x.id === it.dependsOnItemId)?.name ?? '—'} +${it.dependencyOffsetDays}d (${it.dependencyOffsetType === 'WORKING' ? 'laborables' : 'naturals'})`
                          : '—'}
                      </td>
                      <td className="py-1.5 text-right"><button onClick={() => removeItem(it.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-3 grid grid-cols-6 gap-1.5 items-end">
                <input placeholder="Nom de la fase" value={itemForm.name ?? ''} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} className="col-span-2 text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="number" min={1} placeholder="Dies" value={itemForm.durationDays ?? 1} onChange={(e) => setItemForm((f) => ({ ...f, durationDays: Number(e.target.value) }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="number" min={0} placeholder="Hores" value={itemForm.plannedHours ?? 0} onChange={(e) => setItemForm((f) => ({ ...f, plannedHours: Number(e.target.value) }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <select value={itemForm.dependsOnItemId ?? ''} onChange={(e) => setItemForm((f) => ({ ...f, dependsOnItemId: e.target.value || null }))} className="text-xs border border-gray-200 rounded px-2 py-1.5">
                  <option value="">Sense dependència</option>
                  {(selected.items ?? []).map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                </select>
                <button onClick={addItem} className="flex items-center justify-center gap-1 text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={13} /> Afegir</button>
                {itemForm.dependsOnItemId && (
                  <div className="col-span-6 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">Espera des que acaba la predecessora:</span>
                    <input type="number" value={itemForm.dependencyOffsetDays ?? 0} onChange={(e) => setItemForm((f) => ({ ...f, dependencyOffsetDays: Number(e.target.value) }))} className="w-16 text-xs border border-gray-200 rounded px-2 py-1" />
                    <select value={itemForm.dependencyOffsetType} onChange={(e) => setItemForm((f) => ({ ...f, dependencyOffsetType: e.target.value as PlanningDependencyOffsetType }))} className="text-xs border border-gray-200 rounded px-2 py-1">
                      <option value="CALENDAR">dies naturals</option>
                      <option value="WORKING">dies laborables</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
